import { useApiCall } from "@/app/api/apiCall";
import { ListFieldsOptionsApiCall } from "@/app/api/types";
import { MultiValueSubForm } from "@/app/components/form-fields/MultiValueSubForm";
import { SingleValueFormField } from "@/app/components/form-fields/SingleValueFormField";
import { LoadingSelectForm } from "@/app/components/LoadingSelectForm";
import { Entity, IField, ISelectFieldOption } from "@/app/fields/types";
import { useOnFieldChange } from "@/app/hooks/useOnFieldChange";
import { useFormContext } from "@saas-ui/forms";
import { useEffect, useState } from "react";

const fieldTypeHasOptions = <T extends Entity>(field: IField<T>) =>
  ["select"].includes(field.type);

export const FormField = <T extends Entity>({
  field,
  getFieldOptions,
  isDisabled: globalIsDisabled,
}: {
  field: IField<T>;
  getFieldOptions: ListFieldsOptionsApiCall;
  isDisabled?: boolean;
}) => {
  useOnFieldChange(field);
  const handle = useApiCall();
  const isDisabled = globalIsDisabled || field.is_readonly;
  const { resetField } = useFormContext();
  const [options, setOptions] = useState<ISelectFieldOption[] | undefined>(
    fieldTypeHasOptions(field) ? undefined : []
  );
  const { setValue, getValues, trigger } = useFormContext();

  // better to re-fetch the options here so sorting is consistent
  const onAddOption = async (option: ISelectFieldOption) => {
    setOptions(undefined);
    await fetchOptions();
    if (field.type === "select" && field.is_multi) {
      setValue(String(field.field), [
        ...(getValues(String(field.field)) || []),
        option.value,
      ]);
    } else if (field.type === "select") {
      setValue(String(field.field), option.value);
    }
    // re-validate
    await trigger(String(field.field));
  };

  const fetchOptions = async () => {
    await handle(
      () => getFieldOptions(String(field.field)),
      (response) => setOptions(response.results)
    );
  };

  useEffect(() => {
    if (fieldTypeHasOptions(field)) {
      fetchOptions();
    }
    return () => {
      // wipe fields on unmount but don't clear errors
      resetField(String(field.field), { keepError: true });
    };
  }, []);

  if (options === undefined) {
    return <LoadingSelectForm label={field.label} />;
  }

  if (field.type === "select" && field.is_multi) {
    return (
      <MultiValueSubForm
        field={field}
        options={options}
        isDisabled={isDisabled}
        onAddOption={onAddOption}
      />
    );
  }

  return (
    <SingleValueFormField
      field={field}
      options={options}
      isDisabled={isDisabled}
      onAddOption={onAddOption}
    />
  );
};
